import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFavourites } from "apis/myRequestsService";
import { Favourites } from "pages/YourRequest/types";
import { useClickOutside } from "hooks/useClickOutside";
import DefaultAirLogo from "assets/images/logo.svg";

interface HeaderFavouritesDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

// Favourites carry either a full ISO departure/arrival (same shape as the
// favourites list) or an originalDate + HHmm deptim/arrtim pair. Build a Date
// from whichever is available so formatting stays consistent either way.
const resolveDateTime = (
  item: any,
  isoField: "departure" | "arrival",
  timeField: "deptim" | "arrtim",
): Date | null => {
  if (item?.[isoField]) return new Date(item[isoField]);
  if (item?.originalDate && item?.[timeField] != null) {
    const time = String(item[timeField]).padStart(4, "0");
    const hours = time.slice(0, 2);
    const minutes = time.slice(2, 4);
    const base = new Date(item.originalDate);
    if (isNaN(base.getTime())) return null;
    base.setUTCHours(Number(hours), Number(minutes), 0, 0);
    return base;
  }
  return null;
};

const formatDate = (date: Date | null): string => {
  if (!date) return "";
  const month = date.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
  const day = String(date.getUTCDate()).padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${day} ${month} ${year}`;
};

const formatDay = (date: Date | null): string => {
  if (!date) return "";
  return date.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
};

const formatTime = (date: Date | null): string => {
  if (!date) return "";
  return date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });
};

const HeaderFavouritesDropdown: React.FC<HeaderFavouritesDropdownProps> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [favourites, setFavourites] = useState<Favourites[]>([]);
  const [loading, setLoading] = useState(false);
  useClickOutside(containerRef, onClose, isOpen, "mousedown");

  // Refetch on every open: the list changes while the dropdown is closed
  // (e.g. saving a flight from Loads), and the Header never unmounts.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    getFavourites({ page: 1, pageSize: 5 })
      .then((data: any) => {
        if (cancelled) return;
        const items: Favourites[] = data?.items ?? [];
        // Most recently added first.
        const sorted = [...items].sort((a: any, b: any) => {
          const aDate = new Date(a.createdDate ?? 0).getTime();
          const bDate = new Date(b.createdDate ?? 0).getTime();
          return bDate - aDate;
        });
        setFavourites(sorted.slice(0, 5));
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Error fetching favourites:", error);
        setFavourites([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleItemClick = (item: any) => {
    onClose();
    navigate("/favourites", { state: { targetRequestId: item.id } });
  };

  const handleViewAll = () => {
    onClose();
    navigate("/favourites");
  };

  return (
    <div
      ref={containerRef}
      // top-16 alone is a fixed 64px offset from the true viewport top — it
      // doesn't account for the giveaway banner's height pushing the actual
      // header down, so with the banner showing it overlapped the header
      // instead of sitting below it. header-dropdown-mobile-top (below sm)
      // accounts for that; sm:top-full is unaffected since it's positioned
      // relative to the trigger itself, not the viewport, and an inline
      // style would have outranked it, so this is a real class.
      className="fixed left-3 right-3 header-dropdown-mobile-top max-h-[70vh] sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-1 sm:translate-x-40 sm:w-[280px] sm:max-h-[70vh] overflow-hidden rounded-[12px] bg-white shadow-[0_7px_15px_rgba(0,0,0,0.2)] z-50 flex flex-col"
    >
      <div className="bg-[#192227] rounded-t-[12px] px-5 py-2.5 shrink-0">
        <p className="text-white text-base font-semibold leading-tight">
          Favourites
        </p>
      </div>

      <div className="overflow-y-auto px-5">
        {loading ? (
          <div className="py-4 text-center text-xs text-[#5E5E5E]">
            Loading...
          </div>
        ) : favourites.length === 0 ? (
          <div className="py-4 text-center text-xs text-[#5E5E5E]">
            No saved flights yet.
          </div>
        ) : (
          favourites.map((item: any) => {
            const departureDate = resolveDateTime(item, "departure", "deptim");
            const arrivalDate = resolveDateTime(item, "arrival", "arrtim");
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleItemClick(item)}
                className="w-full text-left py-2.5 border-b border-[#EFEFEF] last:border-b-0 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[#192227] text-sm font-semibold truncate min-w-0">
                    {item.airlineName}{" "}
                    <span className="text-xs font-normal text-[#5E5E5E]">
                      {item.flightNo ? `(${item.flightNo})` : ""}
                    </span>
                  </p>
                  <p className="text-[#192227] text-sm font-semibold whitespace-nowrap flex-shrink-0 mt-0.5">
                    {item.sourceCode} → {item.destinationCode}
                  </p>
                </div>
                {/* Full row width so the date/time stays on one line without
                    widening the dropdown. */}
                <p className="text-[#5E5E5E] text-[11px] leading-4 font-medium mt-0.5 whitespace-nowrap overflow-hidden text-ellipsis">
                  {formatDate(departureDate)}
                  {formatDate(departureDate) && formatDay(departureDate) ? " . " : ""}
                  {formatDay(departureDate)}
                  {formatTime(departureDate) ? ` ${formatTime(departureDate)}` : ""}
                  {formatTime(arrivalDate) ? ` - ${formatTime(arrivalDate)}` : ""}
                </p>
              </button>
            );
          })
        )}
      </div>

      <button
        type="button"
        onClick={handleViewAll}
        className="flex items-center gap-1 px-5 py-2.5 text-xs font-medium text-black hover:bg-gray-50 transition-colors shrink-0 self-start"
      >
        <span className="border-b border-dashed border-black pb-0.5">
          View all Favourites
          
        </span>
        <svg
          className="w-3 h-3 mt-0.5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 18l6-6-6-6"
          />
        </svg>
      </button>
    </div>
  );
};

export default HeaderFavouritesDropdown;
